'use client'

import type { MouseEvent } from 'react'

import { Button } from '@/app/components/atoms/Button'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import {
  ADD_TO_CART_PRICE_TYPE,
  PDP_BUY_CTA_CLICK_MODULE_NAME,
  PDP_BUY_CTA_CLICK_MODULE_TYPE,
  PDP_BUY_CTA_ITEM_LIST_ID,
  PDP_BUY_CTA_ITEM_LIST_NAME,
  NO_APLICA,
  resolveCtaDestination,
} from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'

import type { PurchaseButtonProps } from './types'

/** Tope de espera (ms) a la confirmación de GTM antes de navegar igualmente. */
const ADD_TO_CART_EVENT_TIMEOUT_MS = 1000

/**
 * `PurchaseButton` — botón de compra principal, listo para consumirse en la
 * futura PDP (specs/019-estados-compra-eventos, research.md §8: este feature
 * no conecta la página de detalle real, solo entrega esta pieza reutilizable).
 * Mismo criterio de habilitado/deshabilitado que las cards
 * (`shared/cards/PurchaseCta`): estado por defecto → enlace a `href`
 * (`purchase_url`); cualquier otro estado → botón deshabilitado con el texto
 * resuelto por `resolvePurchaseState`.
 *
 * Analítica (`Comprar.csv` — ÚLTIMO EVENTO EN SCOPE, guía `01_Scope.md`): el
 * único botón habilitado (`purchaseState.ctaEnabled && href`) es el que
 * redirige al checkout externo del vendor, así que es el único que dispara
 * `click` + `add_to_cart`. Ambos se empujan de forma SÍNCRONA en el `onClick`,
 * antes de que el navegador procese la navegación del propio `<a href>` (no
 * hay `preventDefault`): el `dataLayer.push` dispara los triggers de GTM en el
 * mismo tick, así que basta con no retrasar el push — la entrega efectiva vía
 * beacon ante la salida de página es responsabilidad de la etiqueta GA4 en GTM
 * (`transport_type: 'beacon'`, guía `08_Implementacion_QA.md` #12, tarea del
 * analista/tagging specialist, no del frontend).
 */
export function PurchaseButton({
  purchaseState,
  href,
  className,
  inverse = false,
  stretch = true,
  analyticsItem,
  ctaPosition,
}: PurchaseButtonProps) {
  const analytics = useOptionalPageAnalytics()
  const variant = inverse ? 'filled-accent' : 'filled'

  if (purchaseState.ctaEnabled && href) {
    const destination = href
    function handleClick(event: MouseEvent<HTMLAnchorElement>) {
      if (!analyticsItem || !ctaPosition || !analytics) return
      const { host, vendor } = resolveCtaDestination(href)

      analytics.trackClick({
        click_type: 'link',
        click_subtype: NO_APLICA,
        link_text: slugify(purchaseState.ctaLabel),
        // Solo host (sin path ni query, que pueden llevar tokens del vendor).
        link_url: host,
        link_domain: NO_APLICA,
        module_name: PDP_BUY_CTA_CLICK_MODULE_NAME,
        module_type: PDP_BUY_CTA_CLICK_MODULE_TYPE,
        nav_level: NO_APLICA,
        nav_path: NO_APLICA,
        action_detail: NO_APLICA,
        action_value: NO_APLICA,
        action_context: NO_APLICA,
      })

      // Garantía de entrega: se retiene la navegación hasta que GTM confirma
      // (`eventCallback`) o vence el timeout, con guard para navegar UNA vez.
      // Clics con modificador / botón no principal (nueva pestaña) no se
      // interceptan: la página actual sigue viva y el push llega igualmente.
      const interceptNavigation =
        event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
      let navigated = false
      const navigate = () => {
        if (navigated) return
        navigated = true
        window.location.assign(destination)
      }
      if (interceptNavigation) {
        event.preventDefault()
        window.setTimeout(navigate, ADD_TO_CART_EVENT_TIMEOUT_MS)
      }

      analytics.trackAddToCart({
        currency: analyticsItem.currency,
        value: analyticsItem.price,
        item_list_id: PDP_BUY_CTA_ITEM_LIST_ID,
        item_list_name: PDP_BUY_CTA_ITEM_LIST_NAME,
        cta_position: ctaPosition,
        cta_destination_host: host,
        cta_destination_vendor: vendor,
        items: [{ ...analyticsItem, price_type: ADD_TO_CART_PRICE_TYPE }],
        eventCallback: navigate,
        eventTimeout: ADD_TO_CART_EVENT_TIMEOUT_MS,
      })
    }

    return (
      <Button
        href={href}
        variant={variant}
        size="l"
        stretch={stretch}
        className={className}
        onClick={handleClick}
      >
        {purchaseState.ctaLabel}
      </Button>
    )
  }

  return (
    <Button
      type="button"
      disabled
      variant={variant}
      size="l"
      stretch={stretch}
      className={className}
    >
      {purchaseState.ctaLabel}
    </Button>
  )
}
